import { useEffect, useRef, useState } from "react";
import Editor, { type OnMount } from "@monaco-editor/react";
import {
  AlertTriangle,
  CheckCircle2,
  KeyRound,
  Loader2,
  Play,
  RotateCw,
  XCircle,
} from "lucide-react";

import { QuestionPrompt, type QuestionPictureInfo } from "@/components/QuestionPrompt";
import { EditorHelpDialog } from "@/components/candidate/EditorHelpDialog";
import { EditorToolbar } from "@/components/candidate/EditorToolbar";
import { OwnTestResults, OwnTestsCard } from "@/components/candidate/OwnTests";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { TestCase } from "@/lib/api";
import { useEditorPrefs } from "@/lib/editorPrefs";
import { configureMonaco } from "@/lib/monaco";
import type { OwnTest, OwnTestResult } from "@/lib/ownTests";
import { EDITOR_MODE, languageEntry, SANDBOX_NAME, type Language } from "@/lib/runtimes";
import { AUTHORING } from "@/lib/runtimes/authoring";
import {
  isJsContract,
  languageNote,
  type QuestionContract,
  type RunLocation,
} from "@/lib/runtimes/registry";
import type { RunFailure } from "@/lib/runtimes/serverRun";

// Mirror of backend/api/candidate.py `_MAX_CODE_LEN` — the server 422s a
// longer `final_code`, and the save queue lets a twice-refused save go (R13,
// pre-ship 2026-10-18). Monaco has no maxLength, so the question screen warns
// past it instead.
export const CODE_MAX = 100_000;

export interface CodeRunResult {
  name: string;
  passed: boolean;
  output?: string;
  error?: string;
}

interface CodeQuestionProps {
  promptMd: string;
  /** EP-19: the picture under the prompt, if the question has one. */
  picture?: QuestionPictureInfo | null;
  exampleTests: TestCase[];
  starterCode: string;
  onChange: (code: string) => void;
  onRun: () => void;
  running: boolean;
  results: CodeRunResult[] | null;
  pyodideLoading: boolean;
  // Optional so lower-stakes callers (e.g. Practice) can skip the retry banner;
  // when omitted, pyodideError stays false and the banner never renders.
  pyodideError?: boolean;
  onRetryPyodide?: () => void;
  // Cheat-proof challenges: this attempt's ChallengeToken. Present only when
  // the test has the option on AND there is an attempt (or the recruiter
  // preview's fixed sample token). Practice never passes one, so neither the
  // callout nor the "Final output" column renders there. The server already
  // twisted `exampleTests[].expected_output` and prepended CHALLENGE_TOKEN to
  // `starterCode` — this component only explains, it never transforms.
  challengeToken?: string | null;
  /** FEAT-6: the question's language — the recruiter's choice, frozen in the
   *  snapshot. Names the sandbox and picks the editor mode. Default Python. */
  language?: Language;
  /** FEAT-6 (SQL only): the schema + seed rows every test runs before the
   *  candidate's query. READ-ONLY here and required reading — without it the
   *  candidate cannot know the table or column names. Never answer-key
   *  material: the expected rows live in the (stripped) hidden tests. */
  setupCode?: string | null;
  /** FEAT-12: where "Run tests" executes. "server" = the code runner (C / C++
   *  / Go): the Run says "Running on our servers…", a compile error and the
   *  throttle / busy lines replace the sandbox Alert. Default "browser" —
   *  every existing caller renders exactly as before. */
  runLocation?: RunLocation;
  /** FEAT-12: the compiler's output when the last server run did not compile. */
  compileError?: string | null;
  /** FEAT-12: why the last server run produced no results. */
  runFailure?: RunFailure | null;
  /** FEAT-18: the code the toolbar's Reset puts back — the question's OWN
   *  starter. ⚠ Not `starterCode`: that is the editor's first content, which
   *  on the real test is the candidate's saved code. Omitted = no Reset
   *  button (a submitted position on a forward-only test). */
  resetTo?: string | null;
  /** FEAT-18: the editor has just been reset. The edit is already made (and
   *  `onChange` has fired with it); the owner clears what the old code left
   *  behind — results, and its saved copy on the server. */
  onReset?: () => void;
  /** FEAT-18: the "How the code editor works" text for the help panel — the
   *  workspace's own or the platform default. Omitted = the built-in default. */
  helpIntro?: string | null;
  /** FEAT-18: the help panel was opened (the owner may fetch `helpIntro` now). */
  onHelpOpen?: () => void;
  /** FEAT-18: activity monitoring is on — the help panel then says which
   *  shortcuts are recorded. Never set by Practice or the recruiter's Preview. */
  monitored?: boolean;
  /** FEAT-18: the fullscreen gate is up. Every overlay of the candidate page
   *  is `z-50`, and this component renders AFTER the gate in DOM order — so
   *  its confirm, help panel and settings must step aside or they cover it. */
  overlaysBlocked?: boolean;
  /** FEAT-31: the code replay's paste hooks. Set ONLY by the real test, and
   *  only while the attempt records a replay — its presence also shows the
   *  "recorded" caption and the help panel's sentence. ⚠ Never passed by
   *  Practice or the recruiter's Preview (`codeReplayRecorder.test.ts`). */
  replayHooks?: { beforePaste: () => void; notePaste: () => void };
  /** FEAT-29: the candidate's own test cases — the "Your tests" card under
   *  the example tests, and their results under the example tests' results.
   *  The OWNER runs them (in the tab, or with the server Run) and keeps the
   *  results apart from `results`, which is what gets saved with the answer.
   *  Omitted = no card. */
  ownTests?: {
    tests: OwnTest[];
    onChange: (tests: OwnTest[]) => void;
    /** The last Run's results for them; null = none to show. */
    results: OwnTestResult[] | null;
    /** The question's contract — what an input and an expected value are. */
    contract: QuestionContract;
  };
}

type CodeEditor = Parameters<OnMount>[0];

// FEAT-12 (spec §3.5) — the candidate's lines for a server run. No run
// counter anywhere: a limit is explained only when it is hit.
function runFailureText(failure: RunFailure, secondsLeft: number): string {
  switch (failure.kind) {
    case "busy":
      return "Our servers are busy right now — try again in a moment. You can still write and submit your code; it's graded after you finish.";
    case "throttled":
      return secondsLeft > 0
        ? `You've run tests a lot in a short time. Wait ${secondsLeft} seconds and try again.`
        : "You can run your tests again now.";
    case "capped":
      return "You've run your code many times on this test. You can still submit it — it's graded after you finish.";
    case "in_flight":
      return "Your previous run is still going — wait for it to finish.";
    case "network":
      return "We couldn't reach our servers. Check your connection and try again.";
    // The recruiter's test Preview renders this component too; its run-code
    // door refuses below Pro (403) or without the runner (409) — the same
    // lines as CodeEditorModal, never the candidate's "you can still submit".
    case "locked":
      return "Running code in this language needs the Pro plan.";
    case "unavailable":
      return "This language runs on our servers, which are not available on this server yet.";
    default:
      return "Your code couldn't be run just now. You can still submit it — it's graded after you finish.";
  }
}

// The candidate-facing wording of the twist, keyed off the question's
// language (FEAT-6). Must match backend/cheat_proof.py:
//   python:     twisted = str(result)[::-1] + ":" + token[::-1]
//   javascript (and typescript — FEAT-12, the same contract once its types
//   are erased): plain = result if it is a string, else JSON.stringify(result)
//               (key order preserved); twisted = plain reversed + ":" + token reversed
//   sql:        EXEMPT — the server never sets a token, so this never renders.
function reverseString(s: string): string {
  return Array.from(s).reverse().join("");
}

function ChallengeCallout({ token, language }: { token: string; language: Language }) {
  const reversed = reverseString(token);
  const js = isJsContract(language);
  return (
    <Card
      className="border-info/40 bg-info/5"
      data-testid="cheat-proof-callout"
      data-language={language}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4 text-info" aria-hidden="true" />
          Cheat-proof challenge
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p>
          Your ChallengeToken is{" "}
          <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-base font-semibold tracking-wide">
            {token}
          </code>
          . It is already defined at the top of your editor as{" "}
          <code className="font-mono">{js ? "const CHALLENGE_TOKEN" : "CHALLENGE_TOKEN"}</code>.
        </p>
        {js ? (
          <p>
            Don&apos;t return your answer as-is. If it is a string use it as it
            is, otherwise turn it into a string with{" "}
            <code className="font-mono">JSON.stringify</code>; reverse that
            string, then add a colon and your ChallengeToken reversed &mdash;
            and return the result as a string.
          </p>
        ) : (
          <p>
            Don&apos;t return your answer as-is. Convert it to a string with{" "}
            <code className="font-mono">str()</code>, reverse that string, then
            add a colon and your ChallengeToken reversed &mdash; and return the
            result as a string.
          </p>
        )}
        <p className="text-muted-foreground">
          Example: an answer of{" "}
          <code className="font-mono">{js ? "[1, 2]" : "9-9-946"}</code> becomes{" "}
          <code className="font-mono">{js ? "]2,1[" : "649-9-9"}:{reversed}</code>. The{" "}
          <em>Final output</em> column of each example test shows exactly what
          to return.
        </p>
      </CardContent>
    </Card>
  );
}

export function CodeQuestion({
  promptMd,
  picture,
  exampleTests,
  starterCode,
  onChange,
  onRun,
  running,
  results,
  pyodideLoading,
  pyodideError = false,
  onRetryPyodide,
  challengeToken,
  language = "python",
  setupCode = null,
  runLocation = "browser",
  compileError = null,
  runFailure = null,
  resetTo,
  onReset,
  helpIntro,
  onHelpOpen,
  monitored = false,
  overlaysBlocked = false,
  replayHooks,
  ownTests,
}: CodeQuestionProps) {
  const schema = (setupCode ?? "").trim();
  const onServer = runLocation === "server";
  // FEAT-29: the own cases' results of the last Run, if any.
  const ownResults = ownTests?.results?.length ? ownTests.results : null;
  // FEAT-18: the editor's settings (one store for every question of the test)
  // and the editor instance Reset writes through.
  const prefs = useEditorPrefs();
  const editorRef = useRef<CodeEditor | null>(null);
  const [editorReady, setEditorReady] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const canReset = resetTo !== undefined && resetTo !== null;
  // The sandbox spinner stands in for the editor only until the editor first
  // exists. A LATER load — "Retry loading sandbox" — leaves it mounted: a
  // remount re-reads `starterCode` (on the real test, the SAVED code) while
  // the owner still holds what the candidate typed, so the editor showed one
  // code and Run / Next used another. Kept mounted, it also keeps its undo
  // history and the instance Reset writes through.
  const sandboxPlaceholder = pyodideLoading && !editorReady;
  const sandboxReloading = pyodideLoading && editorReady;
  // Reset REPLACES THE TEXT IN PLACE — no remount, so Monaco's read-once
  // `defaultValue` never comes into it — and as ONE undoable edit: Ctrl+Z
  // brings the candidate's code back. `executeEdits` fires the editor's
  // change event, so `onChange` has the starter before `onReset` runs.
  const resetToStarter = () => {
    setResetConfirmOpen(false);
    const editor = editorRef.current;
    const model = editor?.getModel();
    if (!editor || !model || !canReset) return;
    editor.pushUndoStop();
    editor.executeEdits("reset-to-starter", [
      { range: model.getFullModelRange(), text: resetTo ?? "", forceMoveMarkers: true },
    ]);
    editor.pushUndoStop();
    editor.setPosition({ lineNumber: 1, column: 1 });
    editor.focus();
    onReset?.();
  };
  // FEAT-31: tell the replay recorder about a paste or a drop INTO THE
  // EDITOR, so the pasted span becomes a step of its own. Hooked on the
  // editor directly — the anti-cheat paste listener exists only while
  // activity monitoring is on, and also fires for the own-test fields.
  // ⚠ A paste is `onDidPaste` ONLY: Monaco's own paste handler listens on the
  // editor's CONTAINER in the capture phase and stops the event, so a DOM
  // `paste` listener here never hears a real Ctrl+V (it only heard pastes
  // into the find widget, wrongly). `onDidPaste` fires right after the change
  // is applied; the recorder splits the typing before it off by itself. A
  // drop has no such event, so it keeps the capture-phase DOM hook, which runs
  // BEFORE Monaco inserts the text.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editorReady || !editor || !replayHooks) return;
    const node = editor.getDomNode();
    const before = (): void => replayHooks.beforePaste();
    node?.addEventListener("drop", before, true);
    const pasted = editor.onDidPaste(() => replayHooks.notePaste());
    return () => {
      node?.removeEventListener("drop", before, true);
      pasted.dispose();
    };
  }, [editorReady, replayHooks]);
  // FEAT-12: a compile can take seconds — after 4 s, say why (aria-live).
  const [slowRun, setSlowRun] = useState(false);
  useEffect(() => {
    if (!running || !onServer) {
      setSlowRun(false);
      return;
    }
    const t = window.setTimeout(() => setSlowRun(true), 4000);
    return () => window.clearTimeout(t);
  }, [running, onServer]);
  // The throttle's countdown (429): Run stays disabled until it reaches 0.
  const [secondsLeft, setSecondsLeft] = useState(0);
  useEffect(() => {
    if (runFailure?.kind !== "throttled") {
      setSecondsLeft(0);
      return;
    }
    setSecondsLeft(runFailure.retryAfterS);
    const t = window.setInterval(
      () => setSecondsLeft((s) => (s <= 1 ? (window.clearInterval(t), 0) : s - 1)),
      1000,
    );
    return () => window.clearInterval(t);
  }, [runFailure]);
  return (
    // 2026-09-27 pre-ship B3: explicit rows below lg. Auto rows let the
    // prompt's content height win the fixed-height grid, and at 375 the
    // editor was left 19 px tall; now the prompt scrolls in a third and the
    // editor + results share two thirds.
    <div className="grid h-[calc(100vh-4rem)] grid-cols-1 grid-rows-[minmax(0,1fr)_minmax(0,2fr)] lg:grid-cols-2 lg:grid-rows-1">
      {/* Left pane: prompt + example tests */}
      <div className="overflow-y-auto border-b border-border lg:border-b-0 lg:border-r">
        <div className="space-y-6 p-6">
          <Card>
            <CardHeader>
              <CardTitle>Problem</CardTitle>
            </CardHeader>
            <CardContent>
              <QuestionPrompt promptMd={promptMd} picture={picture} codeLanguage={language} />
            </CardContent>
          </Card>

          {challengeToken && <ChallengeCallout token={challengeToken} language={language} />}

          {/* FEAT-6: a SQL question is unanswerable without its schema, so the
              setup runs before every test AND is shown here, read-only. */}
          {schema && (
            <Card data-testid="sql-setup-code">
              <CardHeader>
                <CardTitle>Database</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  This runs before your query on every test. You can&apos;t
                  change it.
                </p>
                <pre className="overflow-x-auto rounded bg-secondary p-3 font-mono text-xs">
                  {schema}
                </pre>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Example tests</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {exampleTests.map((tc, idx) => {
                // A twisted case carries the authored output too: show it as
                // "Expected" and the twisted one as "Final output" (what the
                // grader — and the Run button — actually compare against).
                const twisted =
                  tc.original_expected_output !== undefined &&
                  tc.original_expected_output !== null;
                return (
                  <div key={tc.name + idx} className="space-y-2">
                    {idx > 0 && <Separator />}
                    <div className="text-sm font-medium">{tc.name}</div>
                    <div
                      className={cn(
                        "grid grid-cols-1 gap-2",
                        twisted ? "md:grid-cols-3" : "md:grid-cols-2"
                      )}
                    >
                      <div>
                        <div className="text-xs text-muted-foreground">Input</div>
                        <pre className="mt-1 overflow-x-auto rounded bg-secondary p-3 font-mono text-xs">
                          {tc.input}
                        </pre>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Expected</div>
                        <pre className="mt-1 overflow-x-auto rounded bg-secondary p-3 font-mono text-xs">
                          {twisted ? tc.original_expected_output : tc.expected_output}
                        </pre>
                      </div>
                      {twisted && (
                        <div>
                          <div className="text-xs font-medium text-info">
                            Final output
                          </div>
                          <pre className="mt-1 overflow-x-auto rounded border border-info/40 bg-info/5 p-3 font-mono text-xs">
                            {tc.expected_output}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* FEAT-29: the candidate's own cases, right under the examples
              they are modelled on. */}
          {ownTests && (
            <OwnTestsCard
              tests={ownTests.tests}
              onChange={ownTests.onChange}
              language={language}
              contract={ownTests.contract}
              twisted={Boolean(challengeToken)}
              disabled={running}
            />
          )}
        </div>
      </div>

      {/* Right pane: editor + run + results */}
      <div className="flex min-h-0 flex-col">
        {/* Stacked under the prompt on mobile, the editor gets limited height —
            set expectations (DR-4). Hidden once the two-pane layout kicks in. */}
        <p className="border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground lg:hidden">
          The code editor works best on a larger screen.
        </p>
        <EditorToolbar
          language={language}
          canReset={canReset}
          resetDisabled={!editorReady || pyodideLoading || running}
          onResetClick={() => setResetConfirmOpen(true)}
          onHelpClick={() => {
            setHelpOpen(true);
            onHelpOpen?.();
          }}
          overlaysBlocked={overlaysBlocked}
          recording={Boolean(replayHooks)}
        />
        <div className="flex-1 border-b border-border">
          {sandboxPlaceholder ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
              <Loader2 className="h-8 w-8 animate-spin text-info" />
              <div className="text-sm text-muted-foreground">
                Loading {SANDBOX_NAME[language]} sandbox…
              </div>
              <div className="w-full max-w-md space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            </div>
          ) : (
            <Editor
              height="100%"
              beforeMount={configureMonaco}
              defaultLanguage={EDITOR_MODE[language]}
              defaultValue={starterCode}
              onChange={(value) => onChange(value ?? "")}
              onMount={(editor) => {
                editorRef.current = editor;
                setEditorReady(true);
              }}
              theme={prefs.theme}
              options={{
                minimap: { enabled: false },
                fontSize: prefs.fontSize,
                fontFamily: "JetBrains Mono, ui-monospace, monospace",
                lineNumbers: "on",
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: AUTHORING[language].tabSize,
                wordWrap: prefs.wordWrap ? "on" : "off",
              }}
            />
          )}
        </div>

        <div className="border-b border-border bg-card p-4">
          {pyodideError && !pyodideLoading && (
            <Alert className="mb-3 border-destructive/40 bg-destructive/5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                <div className="min-w-0 flex-1 space-y-2">
                  <AlertDescription className="text-sm">
                    The {SANDBOX_NAME[language]} sandbox didn&apos;t load. You can still write and
                    submit your code — it&apos;s graded on our servers.
                  </AlertDescription>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onRetryPyodide}
                    className="cursor-pointer"
                  >
                    <RotateCw className="h-4 w-4" />
                    Retry loading sandbox
                  </Button>
                </div>
              </div>
            </Alert>
          )}
          {onServer && runFailure && !running && (
            <Alert className="mb-3 border-destructive/40 bg-destructive/5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                <AlertDescription className="text-sm" aria-live="polite">
                  {runFailureText(runFailure, secondsLeft)}
                </AlertDescription>
              </div>
            </Alert>
          )}
          {languageNote(language) && (
            <p className="mb-3 text-xs text-muted-foreground" data-testid="language-note">
              {languageNote(language)}
            </p>
          )}
          <Button
            onClick={onRun}
            disabled={running || pyodideLoading || (onServer && secondsLeft > 0)}
            className="w-full cursor-pointer"
          >
            {running ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {onServer ? "Running on our servers…" : "Running tests…"}
              </>
            ) : sandboxReloading ? (
              // The editor stays during a sandbox Retry, so the spinner that
              // used to replace it is said here instead.
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading {SANDBOX_NAME[language]} sandbox…
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Run tests
              </>
            )}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {onServer && running && (
            <p className="mb-3 text-xs text-muted-foreground" aria-live="polite">
              {slowRun
                ? languageEntry(language).uiHint
                  ? `Still running — compiling ${SANDBOX_NAME[language]} can take up to ${
                      languageEntry(language).uiHint
                    }.`
                  : // FEAT-12 Build 2: an interpreted language (PHP, Ruby, Perl)
                    // compiles nothing — no uiHint. Placeholder copy (Emilia's).
                    "Still running — this can take a few seconds."
                : ""}
            </p>
          )}
          {onServer && compileError !== null ? (
            <Alert className="border-destructive/40 bg-destructive/5">
              <div className="flex items-start gap-3">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">Your code didn&apos;t compile</div>
                  <pre className="mt-2 max-h-72 overflow-auto rounded bg-secondary p-2 font-mono text-xs">
                    {compileError}
                  </pre>
                </div>
              </div>
            </Alert>
          ) : results === null && ownResults === null ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Run the visible tests to see results.
            </div>
          ) : (
            <div className="space-y-3">
              {(results ?? []).map((r, idx) => (
                <Alert
                  key={r.name + idx}
                  className={cn(
                    "border",
                    r.passed
                      ? "border-success/40 bg-success/5"
                      : "border-destructive/40 bg-destructive/5"
                  )}
                >
                  <div className="flex items-start gap-3">
                    {r.passed ? (
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                    ) : (
                      <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{r.name}</div>
                      <AlertDescription className="text-xs">
                        {r.passed ? (
                          <span className="text-success">Passed</span>
                        ) : (
                          <div className="space-y-2">
                            <div className="text-destructive">Failed</div>
                            {r.output !== undefined && r.output !== "" && (
                              <div>
                                <div className="text-muted-foreground">Output</div>
                                <pre className="mt-1 overflow-x-auto rounded bg-secondary p-2 font-mono text-xs">
                                  {r.output}
                                </pre>
                              </div>
                            )}
                            {r.error && (
                              <div>
                                <div className="text-muted-foreground">Error</div>
                                <pre className="mt-1 overflow-x-auto rounded bg-secondary p-2 font-mono text-xs">
                                  {r.error}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </AlertDescription>
                    </div>
                  </div>
                </Alert>
              ))}
              {/* FEAT-29: the own cases' results, after the example tests'. */}
              {ownResults && <OwnTestResults results={ownResults} />}
            </div>
          )}
        </div>
        {/* FEAT-18: rendered in place — no portal, the candidate subtree is
            `translate="no"` — and never while the fullscreen gate is up. The
            state survives the gate, so an open panel comes back after it. */}
        {resetConfirmOpen && !overlaysBlocked && (
          <ConfirmDialog
            title="Reset to the starting code?"
            body={
              <>
                <p>
                  Everything you wrote for this question is replaced by the code
                  it started with.
                </p>
                <p>
                  If you change your mind, Undo brings your code back while you
                  stay on this question.
                </p>
              </>
            }
            confirmLabel="Reset code"
            onConfirm={resetToStarter}
            onCancel={() => setResetConfirmOpen(false)}
            isPending={false}
          />
        )}
        {helpOpen && !overlaysBlocked && (
          <EditorHelpDialog
            intro={helpIntro}
            canReset={canReset}
            monitored={monitored}
            replayRecorded={Boolean(replayHooks)}
            ownTests={Boolean(ownTests)}
            onClose={() => setHelpOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

export default CodeQuestion;
